import { Link, Skeleton, Spinner, User } from "@heroui/react";
import { type ClientConfig } from "@sc07-canvas/lib/net";
import {
  MessageCircle,
  Shield,
  TriangleAlert,
  UserRound,
} from "lucide-react";
import { type MouseEvent, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import { useAppContext } from "../../contexts/AppContext";
import { useHasRole } from "../../hooks/useHasRole";
import { useModerator } from "../../Moderator/Moderator";
import { Button } from "../core/Button";

export interface IUser {
  sub: string;
  username: string;
  display_name: string | null;
  picture_url: string | null;
  profile_url: string | null;
  isAdmin: boolean;
  isModerator: boolean;
}

const getMatrixLink = (user: IUser, config: ClientConfig) => {
  return `${config.chat.element_host}/#/user/@${user.sub.replace("@", "=40")}:${config.chat.matrix_homeserver}`;
};

const HighlightHostname = ({ url }: { url: string }) => {
  const parsed = useRef<URL | undefined>(undefined);

  useEffect(() => {
    try {
      parsed.current = new URL(url);
    } catch (_e) {
      /* empty */
    }
  }, [url]);

  if (!parsed.current) return <>{url}</>;

  return (
    <>
      {parsed.current.protocol}//<strong>{parsed.current.host}</strong>
      {parsed.current.pathname}
    </>
  );
};

/**
 * Small UserCard that shows profile picture, display name, full username, message box and (currently unused) view profile button
 * @param param0
 * @returns
 */
export const UserCard = ({
  user,
  hide = [],
}: {
  user?: IUser;
  hide?: ("MOD_ACTIONS" | "VIEW_PROFILE")[];
}) => {
  const { config, setProfile } = useAppContext();
  const [messageStatus, setMessageStatus] = useState<
    "loading" | "no_account" | "has_account" | "error"
  >("loading");
  const MessageStatusIcon =
    messageStatus === "error" ? TriangleAlert : MessageCircle;

  useEffect(() => {
    if (!config) {
      console.warn("[UserCard] config is not available yet");
      return;
    }

    if (!user) {
      return;
    }

    setMessageStatus("loading");

    fetch(
      `https://${config.chat.matrix_homeserver}/_matrix/client/v3/profile/${encodeURIComponent(`@${user.username.replace("@", "=40")}:${config.chat.matrix_homeserver}`)}`,
    )
      .then((req) => {
        if (req.status === 200) {
          setMessageStatus("has_account");
        } else {
          setMessageStatus("no_account");
        }
      })
      .catch((e) => {
        console.error(
          "Error while getting Matrix account details for " + user.sub,
          e,
        );
        setMessageStatus("error");
        toast.error(
          "Error while getting Matrix account details for " + user.sub,
        );
      });
  }, [user, config]);

  const handleMatrixClick = (e: MouseEvent) => {
    if (messageStatus === "no_account") {
      e.preventDefault();

      toast.info("This user has not setup chat yet, you cannot message them");
    }
  };

  const openProfile = () => {
    setProfile(user?.sub);
  };

  if (!user) {
    return (
      <div className="flex flex-col gap-1">
        <div className="flex flex-row space-between p-2">
          <Skeleton className="w-full rounded-lg">
            <User name="" />
          </Skeleton>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-row space-between p-2">
        <User
          name={user?.display_name || user?.username}
          description={<HighlightHostname url={user.sub} />}
          avatarProps={{
            showFallback: true,
            name: undefined,
            src: user?.picture_url || undefined,
          }}
        />
        <div className="ml-auto">
          {config && (
            <Button
              isIconOnly
              as={Link}
              href={getMatrixLink(user, config)}
              target="_blank"
              onClick={handleMatrixClick}
            >
              {messageStatus === "loading" ? (
                <Spinner />
              ) : (
                <MessageStatusIcon aria-hidden="true" size={18} />
              )}
            </Button>
          )}
        </div>
      </div>
      <div className="flex gap-4 items-center *:grow">
        {hide.indexOf("VIEW_PROFILE") === -1 && (
          <Button size="sm" onPress={openProfile}>
            <UserRound aria-hidden="true" size={16} />
            View Profile
          </Button>
        )}
        {hide.indexOf("MOD_ACTIONS") === -1 && <ModActions sub={user.sub} />}
      </div>
    </div>
  );
};

const ModActions = ({ sub }: { sub: string }) => {
  const { dispatch } = useModerator();
  const isMod = useHasRole("MOD");

  if (!isMod) return <></>;

  return (
    <>
      <Button
        size="sm"
        onPress={() => dispatch({ action: "INSPECT_USER", user: sub })}
      >
        <Shield aria-hidden="true" size={16} />
        Mod Menu
      </Button>
    </>
  );
};
